import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from './use-supabase';
import { describeError } from '@ezhire/types';

export interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type ClaudeModelKey = 'claude-opus-4-7' | 'claude-sonnet-4-6' | 'claude-haiku-4-5-20251001';

export interface UseModuleAIOptions {
  moduleSlug: string;
  system?: string;
  /** AI routing-policy key used only when no user model preference applies. */
  taskType?: string;
  /**
   * Explicit model override. If omitted, useModuleAI resolves the model
   * from the current user's `user_model_prefs`:
   *   1. `per_module[moduleSlug]` if set
   *   2. `default_model_key`
   *   3. Fallback: let ai-proxy pick by task_type (no model sent).
   * Any explicit `model` value here wins over prefs.
   */
  model?: ClaudeModelKey | string;
  maxTokens?: number;
  temperature?: number;
}

interface UseModuleAIResult {
  output: string;
  /** The requested or proxy-resolved model for the most recent run. */
  resolvedModel: string | null;
  isStreaming: boolean;
  error: string | null;
  /**
   * True until we've resolved the user's preferred model (or confirmed
   * there are no prefs). Clients should disable AI buttons while this
   * is true to avoid ATLAS-DEEP-021 — the first click pre-resolution
   * previously sent a hardcoded Opus fallback that bypassed routing.
   */
  isReady: boolean;
  run: (messages: AIMessage[]) => Promise<string>;
  cancel: () => void;
  reset: () => void;
}

/**
 * Streaming AI hook for modules. Posts to the ai-proxy edge function,
 * reads the SSE response, and updates `output` incrementally as tokens arrive.
 *
 * ANTHROPIC_API_KEY is never visible to the browser — it lives in the
 * edge function environment only.
 */
export function useModuleAI(opts: UseModuleAIOptions): UseModuleAIResult {
  const supabase = useSupabaseClient();
  const [output, setOutput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ATLAS-DEEP-021: explicit readiness flag so first-click before prefs
  // resolve doesn't silently route to a hardcoded Opus fallback. If
  // opts.model is set, we're ready immediately. If no prefs row exists
  // we let ai-proxy pick by task_type (omit model) rather than burning
  // Opus unnecessarily.
  const [selectedModel, setSelectedModel] = useState<string | null>(opts.model ?? null);
  const [resolvedModel, setResolvedModel] = useState<string | null>(opts.model ?? null);
  const [isReady, setIsReady] = useState<boolean>(!!opts.model);
  const abortRef = useRef<AbortController | null>(null);

  // Resolve the user's preferred model for this module once per mount.
  // Explicit `opts.model` always wins; else per-module override; else default.
  useEffect(() => {
    if (opts.model) {
      setSelectedModel(opts.model);
      setResolvedModel(opts.model);
      setIsReady(true);
      return;
    }
    let cancelled = false;
    setIsReady(false);
    (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) {
          if (!cancelled) {
            setSelectedModel(null);
            setResolvedModel(null);
            setIsReady(true);
          }
          return;
        }
        const { data } = await supabase
          .from('user_model_prefs')
          .select('default_model_key, per_module')
          .eq('user_id', userData.user.id);
        const row = (
          data as Array<{ default_model_key: string; per_module: Record<string, string> }> | null
        )?.[0];
        if (cancelled) return;
        const perModule = row?.per_module?.[opts.moduleSlug];
        // Null => ai-proxy picks by task_type. No more hardcoded Opus.
        const preference = perModule ?? row?.default_model_key ?? null;
        setSelectedModel(preference);
        setResolvedModel(preference);
        setIsReady(true);
      } catch {
        if (!cancelled) {
          setSelectedModel(null);
          setResolvedModel(null);
          setIsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase, opts.moduleSlug, opts.model]);

  const run = useCallback(
    async (messages: AIMessage[]): Promise<string> => {
      setError(null);
      setOutput('');
      setIsStreaming(true);

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');

        // supabaseUrl and supabaseKey are protected on the real client but
        // accessible on the mock. Cast to a loose shape to read them.
        const c = supabase as unknown as { supabaseUrl: string; supabaseKey: string };
        const funcUrl = `${c.supabaseUrl}/functions/v1/ai-proxy`;
        // ATLAS-DEEP-021: only send `model` when we actually have one.
        // Otherwise let ai-proxy pick via task_type / admin mode — which
        // respects runtime routing, chat-mode config, and per-agent
        // prefs. Sending a hardcoded Opus fallback bypassed all of that.
        const chosenModel = opts.model ?? selectedModel;
        const payload: Record<string, unknown> = {
          module_slug: opts.moduleSlug,
          messages,
          system: opts.system,
          max_tokens: opts.maxTokens ?? 2048,
          temperature: opts.temperature,
          stream: true,
        };
        if (chosenModel) payload.model = chosenModel;
        if (opts.taskType) payload.task_type = opts.taskType;
        const res = await fetch(funcUrl, {
          method: 'POST',
          signal: abort.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: c.supabaseKey,
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const err = await res.text();
          throw new Error(err || `HTTP ${res.status}`);
        }
        if (!res.body) throw new Error('No stream body');
        setResolvedModel(res.headers.get('X-Atlas-Model') ?? chosenModel ?? null);

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        let full = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            for (const line of chunk.split('\n')) {
              if (!line.startsWith('data: ')) continue;
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'content_block_delta' && data.delta?.text) {
                  full += data.delta.text;
                  setOutput(full);
                }
              } catch {
                // swallow malformed events
              }
            }
          }
        }
        return full;
      } catch (err) {
        if (abort.signal.aborted) return '';
        const message = describeError(err, 'AI error');
        setError(message);
        throw err;
      } finally {
        setIsStreaming(false);
      }
    },
    [
      opts.moduleSlug,
      opts.system,
      opts.model,
      opts.taskType,
      opts.maxTokens,
      opts.temperature,
      supabase,
      selectedModel,
    ],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const reset = useCallback(() => {
    setOutput('');
    setError(null);
  }, []);

  return { output, resolvedModel, isStreaming, error, isReady, run, cancel, reset };
}
